/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { wrap } from 'popmotion';
import { css, SerializedStyles } from '@emotion/react';
import {
  slideshowWrapper,
  slideshowSlide,
  slideshowControlsWrapper,
  slideshowControlButton,
  slideshowControlsWhite,
} from './style';
import useMeasure from 'react-use-measure';

const variants: Variants = {
  enter: dir => {
    return {
      x: dir > 0 ? '100%' : '-100%',
    };
  },
  center: {
    x: 0,
  },
  exit: dir => {
    return {
      x: dir < 0 ? '100%' : '-100%',
    };
  },
};

const swipeConfidenceThreshold = 10000;

const swipePower = (offset: number, velocity: number) => {
  return Math.abs(offset) * velocity;
};

const SlideShow: React.FC<{
  slides: React.ReactNode[];
  runOnChange: (slide: number, slideIndex: number) => void;
  slideCss?: SerializedStyles;
  slideshowWrapper?: SerializedStyles;
  currSlide?: number;
  whiteCursor?: boolean;
  withButtons?: boolean;
  autoPlay?: boolean;
  interval?: number;
  pauseOnHover?: boolean;
  className?: string;
}> = ({
  currSlide = 0,
  slides,
  runOnChange,
  slideCss,
  whiteCursor = false,
  slideshowWrapper: propsSlideshowWrapper,
  withButtons = true,
  /**
   * Whether to auto play the slideshow
   */
  autoPlay = false,
  /**
   * In milliseconds
   */
  interval = 5000,
  pauseOnHover = true,
  ...props
}) => {
  const [[slide, direction], setPage] = useState([currSlide, 1]);
  const slideIndex = wrap(0, slides.length, slide);
  const [tallestSlide, setTallestSlide] = useState(0);
  const heightGrabbingRef = useRef<HTMLDivElement>(null);
  const [allowScroll, setAllowScroll] = useState(false);
  const [autoPlayInterval, setAutoPlayInterval] = useState<NodeJS.Timeout>();
  const [isAutoPlayPaused, setIsAutoPlayPaused] = useState(false);
  const [measureRef, bounds] = useMeasure();

  useEffect(() => {
    if (allowScroll) {
      const handleTouch = (event: TouchEvent) => {
        event.stopPropagation();
      };
      document.documentElement.addEventListener('touchmove', handleTouch);
      return () => {
        document.documentElement.removeEventListener('touchmove', handleTouch);
      };
    }
  }, [allowScroll]);

  useEffect(() => {
    setPage(([s]) => {
      let diff = currSlide - s;

      return [s + diff, diff];
    });
  }, [currSlide]);

  const startAutoPlay = () => {
    if (!autoPlay) return;
    if (autoPlayInterval) clearInterval(autoPlayInterval);
    setAutoPlayInterval(
      setInterval(() => {
        if (pauseOnHover && isAutoPlayPaused) return;
        paginate(1);
      }, interval)
    );
  };

  useEffect(() => {
    if (autoPlay) {
      startAutoPlay();
    }
  }, [slide, autoPlay, isAutoPlayPaused]);

  const paginate = (pages: number) => {
    setPage(([s]) => {
      let newSlide = s + pages;
      if (newSlide === -1) {
        newSlide = slides.length - 1;
      }
      return [newSlide, pages];
    });
  };

  useEffect(() => {
    if (runOnChange) runOnChange(Number.isNaN(slide) ? 0 : slide, slideIndex);
  }, [slide]);

  useEffect(() => {
    setPage([0, 0]);
  }, [slides.length]);

  useEffect(() => {
    const heights = [...(heightGrabbingRef?.current?.children || [])]?.map(
      child => child?.getBoundingClientRect?.()?.height
    );
    setTallestSlide(Math.max(...heights));
  }, [slides, bounds.width]);

  return (
    <div
      ref={measureRef}
      css={css`
        ${slideshowWrapper};
        min-height: ${tallestSlide}px;
        ${propsSlideshowWrapper}
      `}
      {...props}
    >
      <div
        ref={heightGrabbingRef}
        css={css`
          height: 0px !important;
          max-height: 0px !important;
          overflow: hidden;
          position: absolute;
        `}
      >
        {slides.map((slide, index) => (
          <div
            key={`slide-height-${index}`}
            css={css`
              ${slideshowSlide}
              ${slideCss}
              position: relative;
            `}
          >
            {slide}
          </div>
        ))}
      </div>
      {withButtons && slides.length > 1 && (
        <div
          css={css`
            ${slideshowControlsWrapper}
            ${whiteCursor ? slideshowControlsWhite : null}
          `}
          aria-hidden="true"
        >
          <button
            css={slideshowControlButton}
            onClick={() => paginate(-1)}
            tabIndex={-1}
          ></button>
          <button
            css={slideshowControlButton}
            onClick={() => paginate(1)}
            tabIndex={-1}
          ></button>
        </div>
      )}
      <AnimatePresence initial={false} custom={direction} mode="sync">
        <motion.div
          css={css`
            ${slideshowSlide}
            ${slideCss}
          `}
          key={slide}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.7, ease: [0.32, 0.72, 0, 1] }}
          drag="x"
          dragElastic={0.2}
          dragConstraints={{ left: 0, right: 0 }}
          onDragStart={(event, info) => {
            setAllowScroll(Math.abs(info.delta.y) > Math.abs(info.delta.x));
          }}
          onMouseOver={() => setIsAutoPlayPaused(true)}
          onMouseOut={() => setIsAutoPlayPaused(false)}
          onDragEnd={(e, { offset, velocity }) => {
            const swipe = swipePower(offset.x, velocity.x);

            if (swipe < -swipeConfidenceThreshold) {
              paginate(1);
            } else if (swipe > swipeConfidenceThreshold) {
              paginate(-1);
            }
          }}
        >
          {slides[slideIndex]}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default SlideShow;
